import { api } from "@/lib/apiClient";

export const CHUNK_THRESHOLD = 10 * 1024 * 1024; // auto-chunk files > 10MB
export const CHUNK_SIZE = 5 * 1024 * 1024;

async function uploadSingle(file, { category, matterId, onProgress }) {
  const fd = new FormData();
  fd.append("file", file);
  if (category) fd.append("category", category);
  if (matterId) fd.append("matter_id", matterId);
  const { data } = await api.post("/evidence", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (ev) => onProgress && onProgress(ev.total ? Math.round((ev.loaded * 100) / ev.total) : 0),
  });
  return data;
}

async function uploadChunked(file, { category, matterId, onProgress }) {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const { data: init } = await api.post("/evidence/chunk/init", {
    filename: file.name, content_type: file.type || "application/octet-stream",
    category: category || "", matter_id: matterId || "", total_chunks: totalChunks, file_size: file.size,
  });
  const uid = init.upload_id;
  try {
    for (let c = 0; c < totalChunks; c++) {
      const start = c * CHUNK_SIZE;
      const blob = file.slice(start, Math.min(file.size, start + CHUNK_SIZE));
      const cfd = new FormData();
      cfd.append("index", String(c));
      cfd.append("chunk", blob, `${file.name}.part${c}`);
      await api.post(`/evidence/chunk/${uid}`, cfd, { headers: { "Content-Type": "multipart/form-data" } });
      onProgress && onProgress(Math.round(((c + 1) / totalChunks) * 100));
    }
    const { data } = await api.post(`/evidence/chunk/${uid}/complete`);
    return data;
  } catch (e) {
    try { await api.post(`/evidence/chunk/${uid}/abort`); } catch { /* ignore */ }
    throw e;
  }
}

// Uploads one evidence file, auto-chunking large files (including large .zip archives —
// the chunk-complete endpoint unpacks zips server-side as a background import job).
export async function uploadEvidenceFile(file, opts = {}) {
  return file.size > CHUNK_THRESHOLD
    ? uploadChunked(file, opts)
    : uploadSingle(file, opts);
}

// Runs async tasks with a fixed concurrency limit.
export async function runPool(items, worker, concurrency = 3) {
  let i = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (i < items.length) {
      const idx = i++;
      await worker(items[idx], idx);
    }
  });
  await Promise.all(runners);
}
